import React, { useCallback, useEffect, useState } from 'react';
import './App.css';
import LoginPage from './components/LoginPage';
import ConsentPopup from './components/ConsentPopup';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import CustomerAdminDashboard from './components/CustomerAdminDashboard';
import CustomerUserHome from './components/CustomerUserHome';
import {
  clearSession,
  fetchProfile,
  loadSession,
  login as loginRequest,
  logout as logoutRequest,
  saveSession,
  acceptConsent as acceptConsentRequest,
} from './services/api';

function App() {
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [consentData, setConsentData] = useState(null);
  const [pendingCredentials, setPendingCredentials] = useState(null);

  const bootstrapSession = useCallback(async () => {
    const stored = loadSession();
    if (!stored || !stored.access) {
      setAuthLoading(false);
      return;
    }

    try {
      const profile = await fetchProfile(stored.access);
      const nextSession = { ...stored, profile };
      setSession(nextSession);
      saveSession(nextSession);
    } catch (err) {
      clearSession();
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrapSession();
  }, [bootstrapSession]);

  const handleLogin = async (credentials) => {
    setLoginLoading(true);
    setLoginError('');
    try {
      const response = await loginRequest(credentials);
      
      // Check if consent is required
      if (response.consent_required) {
        setConsentData({
          termsText: response.terms_text,
          username: response.username,
          companyName: response.company_name,
        });
        setPendingCredentials(credentials);
        setLoginLoading(false);
        return;
      }
      
      // Normal login flow
      const profile = await fetchProfile(response.access);
      const nextSession = {
        ...response,
        profile,
      };
      setSession(nextSession);
      saveSession(nextSession);
    } catch (err) {
      const message =
        err?.payload?.detail ||
        err?.payload?.non_field_errors?.[0] ||
        err.message ||
        'Login failed.';
      setLoginError(message);
      clearSession();
    } finally {
      setLoginLoading(false);
    }
  };

  const handleAcceptConsent = async () => {
    if (!pendingCredentials) return;
    
    setLoginLoading(true);
    try {
      const tokens = await acceptConsentRequest(pendingCredentials);
      const profile = await fetchProfile(tokens.access);
      const nextSession = {
        ...tokens,
        profile,
      };
      setSession(nextSession);
      saveSession(nextSession);
      setConsentData(null);
      setPendingCredentials(null);
    } catch (err) {
      const message =
        err?.payload?.detail ||
        err?.payload?.non_field_errors?.[0] ||
        err.message ||
        'Failed to accept consent.';
      setLoginError(message);
      setConsentData(null);
      setPendingCredentials(null);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleCloseConsent = () => {
    setConsentData(null);
    setPendingCredentials(null);
    setLoginError('You must accept the terms and conditions to continue.');
  };

  const handleLogout = async () => {
    // Try to call logout API if we have a session
    if (session?.access) {
      try {
        await logoutRequest(session.access, session.refresh);
      } catch (err) {
        // Log error but continue with local logout
        console.warn('Logout API call failed:', err);
      }
    }
    clearSession();
    setSession(null);
  };

  if (authLoading) {
    return (
      <div className="login-container">
        <div className="login-card">
          <h1>Loading...</h1>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <>
        <LoginPage onSubmit={handleLogin} isLoading={loginLoading} error={loginError} />
        {consentData && (
          <ConsentPopup
            termsText={consentData.termsText}
            username={consentData.username}
            companyName={consentData.companyName}
            onAccept={handleAcceptConsent}
            onClose={handleCloseConsent}
            isLoading={loginLoading}
          />
        )}
      </>
    );
  }

  const role = session.role || session.profile?.role;

  if (role === 'super_admin') {
    return <SuperAdminDashboard session={session} onLogout={handleLogout} />;
  }

  if (role === 'customer_admin') {
    return <CustomerAdminDashboard session={session} onLogout={handleLogout} />;
  }

  return <CustomerUserHome session={session} onLogout={handleLogout} />;
}

export default App;
